import { z } from "zod";
import type { Flag } from "./common";
import {
  RISK_CODE_LIST_V1,
  RISK_CODE_LIST_VERSION,
  type RiskPriority,
} from "./risk-codes.v1";

// TES-1057 (Emelie, point G) + TES-1049 (Ö-281) — the doctor's review of a
// prescription or order document the patient uploaded with F-07.
//
// One entry point for both issues. Kirill sets a status on each uploaded
// document and says whether it matches what she reported. Choosing that it
// deviates is the TES-1049 action: at least one category, a short
// specification for "annat", and the risk flag
// VERIFIERAD_AVVIKELSE_MOT_RECEPTUNDERLAG in Riskflaggor. Emelie (Ö-281):
// "Detta bedöms manuellt av Kirill. Patienten kan inte själv verifiera avvikelsen och AI/OCR får inte ensam skapa signalen."
// So the flag has exactly one source: this doctor action. No form answer,
// no AI and no OCR reaches it (the risk-code entry keeps `condition: null`).
//
// "Komplettering krävs" only records the status for now. The completion
// round it should feed (point D) is TES-1059.

// The statuses and their labels, verbatim from point G.
export const PRESCRIPTION_EVIDENCE_STATUSES = [
  "not_reviewed",
  "verified",
  "insufficient",
  "completion_required",
] as const;
export const prescriptionEvidenceStatusSchema = z.enum(
  PRESCRIPTION_EVIDENCE_STATUSES,
);
export type PrescriptionEvidenceStatus = z.infer<
  typeof prescriptionEvidenceStatusSchema
>;
export const PRESCRIPTION_EVIDENCE_STATUS_LABEL: Record<
  PrescriptionEvidenceStatus,
  string
> = {
  not_reviewed: "Ej granskat",
  verified: "Verifierat",
  insufficient: "Otillräckligt",
  completion_required: "Komplettering krävs",
};

// Whether the document matches what she reported in F-07. The deviation
// label is Emelie's sentence from Ö-281; the match label is point G's
// "överensstämmer med patientens uppgifter".
export const PRESCRIPTION_EVIDENCE_MATCHES = ["matches", "deviates"] as const;
export const prescriptionEvidenceMatchSchema = z.enum(
  PRESCRIPTION_EVIDENCE_MATCHES,
);
export type PrescriptionEvidenceMatch = z.infer<
  typeof prescriptionEvidenceMatchSchema
>;
export const PRESCRIPTION_EVIDENCE_MATCH_LABEL: Record<
  PrescriptionEvidenceMatch,
  string
> = {
  matches: "Överensstämmer med patientens uppgifter",
  deviates: "Uppgifterna avviker från receptunderlaget",
};

// The deviation categories, verbatim and in Emelie's order (Ö-281).
export const PRESCRIPTION_DEVIATION_CATEGORIES = [
  "medication",
  "strength",
  "dose",
  "dosing_frequency",
  "validity",
  "other",
] as const;
export const prescriptionDeviationCategorySchema = z.enum(
  PRESCRIPTION_DEVIATION_CATEGORIES,
);
export type PrescriptionDeviationCategory = z.infer<
  typeof prescriptionDeviationCategorySchema
>;
export const PRESCRIPTION_DEVIATION_CATEGORY_LABEL: Record<
  PrescriptionDeviationCategory,
  string
> = {
  medication: "läkemedel",
  strength: "styrka",
  dose: "dos",
  dosing_frequency: "doseringsfrekvens",
  validity: "receptets eller ordinationens giltighet",
  other: "annat",
};

// "Vid Annat krävs en kort specifikation."
export const PRESCRIPTION_DEVIATION_OTHER_MAX = 500;

// The risk code the deviation raises (TES-1001 list, v1).
export const PRESCRIPTION_DEVIATION_RISK_CODE =
  "VERIFIERAD_AVVIKELSE_MOT_RECEPTUNDERLAG" as const;

// PUT …/transfer-review/uploads/:uploadId/evidence-review — the whole review
// of one document, replacing what was there.
export const prescriptionEvidenceReviewUpdateSchema = z
  .object({
    status: prescriptionEvidenceStatusSchema,
    matchesPatientData: prescriptionEvidenceMatchSchema.nullable(),
    deviationCategories: z
      .array(prescriptionDeviationCategorySchema)
      .max(PRESCRIPTION_DEVIATION_CATEGORIES.length)
      .default([]),
    otherSpecification: z
      .string()
      .trim()
      .max(PRESCRIPTION_DEVIATION_OTHER_MAX)
      .nullable()
      .optional(),
  })
  .superRefine((v, ctx) => {
    const categories = new Set(v.deviationCategories);
    if (categories.size !== v.deviationCategories.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["deviationCategories"],
        message: "duplicate_category",
      });
    }
    // Comparing the document with her answers is part of reviewing it.
    if (v.status === "not_reviewed" && v.matchesPatientData !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["matchesPatientData"],
        message: "review_required",
      });
    }
    if (v.matchesPatientData === "deviates") {
      // "Han väljer därefter minst en kategori"
      if (categories.size === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["deviationCategories"],
          message: "category_required",
        });
      }
      if (categories.has("other") && !v.otherSpecification) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["otherSpecification"],
          message: "specification_required",
        });
      }
      if (!categories.has("other") && v.otherSpecification) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["otherSpecification"],
          message: "specification_without_other",
        });
      }
    } else if (categories.size > 0 || v.otherSpecification) {
      // Categories belong to a deviation only.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["deviationCategories"],
        message: "categories_without_deviation",
      });
    }
  });
export type PrescriptionEvidenceReviewUpdate = z.input<
  typeof prescriptionEvidenceReviewUpdateSchema
>;

// The review of one document as the transfer review shows it. A document
// nobody has reviewed yet reads as `not_reviewed` with no reviewer.
export const prescriptionEvidenceReviewSchema = z.object({
  uploadId: z.string().uuid(),
  status: prescriptionEvidenceStatusSchema,
  matchesPatientData: prescriptionEvidenceMatchSchema.nullable(),
  deviationCategories: z.array(prescriptionDeviationCategorySchema),
  // The doctor's short text for "annat"; null otherwise.
  otherSpecification: z.string().nullable(),
  reviewedBy: z.object({ name: z.string() }).nullable(),
  reviewedAt: z.string().nullable(),
});
export type PrescriptionEvidenceReview = z.infer<
  typeof prescriptionEvidenceReviewSchema
>;

export function unreviewedPrescriptionEvidence(
  uploadId: string,
): PrescriptionEvidenceReview {
  return {
    uploadId,
    status: "not_reviewed",
    matchesPatientData: null,
    deviationCategories: [],
    otherSpecification: null,
    reviewedBy: null,
    reviewedAt: null,
  };
}

/**
 * The chosen categories in Emelie's order, "annat" with its specification:
 * "dos, annat (gammal förpackning)".
 */
export function deviationCategoriesText(
  categories: readonly PrescriptionDeviationCategory[],
  otherSpecification: string | null | undefined,
): string {
  return PRESCRIPTION_DEVIATION_CATEGORIES.filter((c) => categories.includes(c))
    .map((c) =>
      c === "other" && otherSpecification
        ? `${PRESCRIPTION_DEVIATION_CATEGORY_LABEL.other} (${otherSpecification})`
        : PRESCRIPTION_DEVIATION_CATEGORY_LABEL[c],
    )
    .join(", ");
}

// The flag fields a deviation carries, from the risk-code list entry: its
// code, the list version, its priority (null: v1 states none) and its
// destination. The list is the single source; nothing here re-grades it.
export function prescriptionDeviationFlag(): Flag {
  const entry = RISK_CODE_LIST_V1.find(
    (e) => e.code === PRESCRIPTION_DEVIATION_RISK_CODE,
  );
  const priority: RiskPriority | null = entry?.priority ?? null;
  return {
    rule: PRESCRIPTION_DEVIATION_RISK_CODE,
    // The list's sorting weight for an unstated priority; never acute.
    severity:
      priority === "high" ? "high" : priority === "normal" ? "low" : "medium",
    escalate: "none",
    riskCode: PRESCRIPTION_DEVIATION_RISK_CODE,
    riskListVersion: RISK_CODE_LIST_VERSION,
    riskPriority: priority,
    riskDestination: entry?.destination ?? "doctor_risk_flags",
  };
}
